import { useRef, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { AdaptiveDpr, AdaptiveEvents } from "@react-three/drei";
import * as THREE from "three";

/* ─── Geometric Owl ─── */
function GeometricOwl() {
  const group = useRef<THREE.Group>(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useFrame(({ clock, pointer }) => {
    if (!group.current) return;
    const t = clock.getElapsedTime();
    // slow rotation
    group.current.rotation.y += 0.003;
    // bob up/down
    group.current.position.y = Math.sin(t * 0.8) * 0.1;
    // tilt toward mouse
    mouseRef.current.x += (pointer.x * 0.26 - mouseRef.current.x) * 0.05;
    mouseRef.current.y += (pointer.y * 0.26 - mouseRef.current.y) * 0.05;
    group.current.rotation.x = mouseRef.current.y;
    group.current.rotation.z = -mouseRef.current.x * 0.5;
  });

  const goldMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#D4A843",
        emissive: "#D4A843",
        emissiveIntensity: 0.15,
        wireframe: true,
        metalness: 0.8,
        roughness: 0.2,
      }),
    []
  );

  return (
    <group ref={group} scale={1.1}>
      {/* Head */}
      <mesh material={goldMat}>
        <icosahedronGeometry args={[1, 1]} />
      </mesh>
      {/* Left ear */}
      <mesh position={[-0.6, 0.9, 0]} rotation={[0, 0, 0.3]} material={goldMat}>
        <coneGeometry args={[0.25, 0.6, 4]} />
      </mesh>
      {/* Right ear */}
      <mesh position={[0.6, 0.9, 0]} rotation={[0, 0, -0.3]} material={goldMat}>
        <coneGeometry args={[0.25, 0.6, 4]} />
      </mesh>
      {/* Left eye */}
      <mesh position={[-0.32, 0.15, 0.85]} material={goldMat}>
        <torusGeometry args={[0.15, 0.03, 8, 16]} />
      </mesh>
      {/* Right eye */}
      <mesh position={[0.32, 0.15, 0.85]} material={goldMat}>
        <torusGeometry args={[0.15, 0.03, 8, 16]} />
      </mesh>
      {/* Beak */}
      <mesh position={[0, -0.1, 0.95]} rotation={[Math.PI, 0, 0]} material={goldMat}>
        <coneGeometry args={[0.1, 0.2, 3]} />
      </mesh>
    </group>
  );
}

/* ─── Orbiting Particles ─── */
function FloatingParticles({ count }: { count: number }) {
  const ref = useRef<THREE.Points>(null);

  const { positions, speeds } = useMemo(() => {
    const p = new Float32Array(count * 3);
    const s = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const phi = Math.random() * Math.PI * 2;
      const theta = Math.random() * Math.PI;
      const r = 1.5 + Math.random() * 1.5;
      p[i * 3] = r * Math.sin(theta) * Math.cos(phi);
      p[i * 3 + 1] = r * Math.sin(theta) * Math.sin(phi);
      p[i * 3 + 2] = r * Math.cos(theta);
      s[i] = 0.001 + Math.random() * 0.004;
    }
    return { positions: p, speeds: s };
  }, [count]);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const geo = ref.current.geometry;
    const pos = geo.attributes.position.array as Float32Array;
    const t = clock.getElapsedTime();
    for (let i = 0; i < count; i++) {
      const speed = speeds[i];
      const ix = i * 3;
      const x = pos[ix];
      const z = pos[ix + 2];
      pos[ix] = x * Math.cos(speed) - z * Math.sin(speed);
      pos[ix + 2] = x * Math.sin(speed) + z * Math.cos(speed);
      pos[ix + 1] += Math.sin(t + i) * 0.0005;
    }
    geo.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
          count={count}
        />
      </bufferGeometry>
      <pointsMaterial
        color="#D4A843"
        size={0.03}
        transparent
        opacity={0.6}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

/* ─── Background Planes ─── */
function BackgroundPlanes() {
  const ref = useRef<THREE.Group>(null);
  useFrame(() => {
    if (ref.current) ref.current.rotation.z += 0.0005;
  });
  return (
    <group ref={ref} position={[0, 0, -3]}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} rotation={[0, 0, (i * Math.PI) / 3]}>
          <planeGeometry args={[8, 8]} />
          <meshBasicMaterial color="#0a0a1a" wireframe transparent opacity={0.05} />
        </mesh>
      ))}
    </group>
  );
}

/* ─── Canvas Scene ─── */
function Scene() {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  return (
    <>
      <ambientLight intensity={0.3} color="#fff5e6" />
      <pointLight position={[3, 3, 3]} color="#D4A843" intensity={1.5} />
      <pointLight position={[-3, -2, 2]} color="#1a1a3e" intensity={0.8} />
      <GeometricOwl />
      <FloatingParticles count={isMobile ? 80 : 200} />
      <BackgroundPlanes />
      <AdaptiveDpr pixelated />
      <AdaptiveEvents />
    </>
  );
}

export default function HeroScene() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      <Canvas
        camera={{ fov: 45, position: [0, 0, 5] }}
        gl={{ alpha: true, antialias: true, toneMapping: THREE.ACESFilmicToneMapping }}
        dpr={[1, 2]}
        style={{ background: "transparent" }}
      >
        <Scene />
      </Canvas>
    </div>
  );
}
